// src/validation/orphanDetection.js
// WP5 — Graph-wide orphan / duplicate detection (spec §2d, §WP5).
//
// Pure, additive, session-level pass over ALL captured rows: any ad-request
// row that is not claimed by any lifecycle (i.e. never appears in a
// `lifecycle.edges[]` entry or a `phases.*.rowKey`) is surfaced as either
// a "duplicate" (shares a hard identity — correlator+iu, or a beacon
// URL/nurl — with an already-claimed row) or a genuine "orphan" (no shared
// identity with anything claimed).
//
// This module does NOT touch `buildSlotLifecycles` or `detectRedundancies`
// — it consumes their output (`lifecycles`, each with `edges[]`/`phases`)
// plus the full unfiltered row set (`allRows`) and produces a session-level
// report only. Never throws — bad input always degrades to the empty shape.

// Mirrors proxy-server/conformance/discoverSignals.js `AD_REQUEST_TYPES` —
// the pool of request types that SHOULD belong to some lifecycle. Keep in
// sync; do not prune types here without pruning there too.
const AD_REQUEST_TYPES = new Set([
  'google', 'gam_video', 'ima', 'prebid', 'amazon', 'amazon_video',
  'google_impression', 'google_click', 'amazon_impression', 'bidder_impression',
  'prebid_win', 'omid_impression', 'omid_measurement', 'omid_session',
  'springserve_vast', 'sdk_bidding',
]);

const EMPTY_REPORT = () => ({
  orphans: [],
  duplicates: [],
  counts: { total: 0, claimed: 0, orphans: 0, duplicates: 0 },
  reasons: [],
});

const rowKeyOf = (r) => (r && (r._k ?? r.id)) ?? null;
const rowTypeOf = (r) => String(r?.type || r?.requestType || '').toLowerCase();

const toMs = (v) => {
  if (!v && v !== 0) return 0;
  if (typeof v === 'number') return v;
  const n = Date.parse(v);
  return Number.isNaN(n) ? 0 : n;
};
const tsOf = (r) =>
  toMs(r?.startedAt) ||
  toMs(r?.startedAtMs) ||
  toMs(r?.timing?.start) ||
  toMs(r?.startedAtISO) ||
  toMs(r?.timestamp) ||
  0;

// Defensive query-param read — never throws on a malformed/missing URL.
function qp(url, key) {
  try { return new URL(url).searchParams.get(key) || ''; } catch { return ''; }
}

// Best-effort correlator+iu identity for a row (GAM requests + beacons that
// carry a correlator). Returns null when neither signal is present.
function correlatorIuKey(row) {
  try {
    const url = row?.url || '';
    const vd = row?.validData || {};
    const correlator = vd.correlator || qp(url, 'correlator') || null;
    const iu = vd.adSlot || qp(url, 'iu') || null;
    if (!correlator || !iu) return null;
    return `${correlator}::${iu}`;
  } catch { return null; }
}

// Best-effort win/impression-beacon identity: the beacon's own URL, or a
// `nurl`/`lurl` it carries, used to line it up against a claimed row that
// emitted/consumes the same URL (see WP0/WP3 nurl surfacing).
function beaconIdentity(row) {
  try {
    const url = row?.url || '';
    const nurl = row?.validData?.nurl || row?.validData?.lurl || qp(url, 'nurl') || qp(url, 'lurl') || null;
    return { url: url || null, nurl: nurl || null };
  } catch { return { url: null, nurl: null }; }
}

/**
 * Build the claimed-rowKey set from a list of lifecycle objects (as produced
 * by `buildSlotLifecycles`). A row is "claimed" if its key appears anywhere
 * in any lifecycle: as an edge endpoint (`"<role>:<rowKey>"`, prefix
 * stripped), as a `phases.*.rowKey`, or as the lifecycle's own `key`
 * (== the GAM row's key).
 */
function buildClaimedSet(lifecycles) {
  const claimed = new Set();
  if (!Array.isArray(lifecycles)) return claimed;

  for (const lc of lifecycles) {
    if (!lc || typeof lc !== 'object') continue;

    if (lc.key != null) claimed.add(String(lc.key));

    if (Array.isArray(lc.edges)) {
      for (const edge of lc.edges) {
        if (!edge) continue;
        for (const nodeId of [edge.from, edge.to]) {
          if (typeof nodeId !== 'string') continue;
          const idx = nodeId.indexOf(':');
          const stripped = idx >= 0 ? nodeId.slice(idx + 1) : nodeId;
          if (stripped) claimed.add(stripped);
        }
      }
    }

    const phases = lc.phases;
    if (phases && typeof phases === 'object') {
      for (const phaseKey of Object.keys(phases)) {
        const phase = phases[phaseKey];
        if (!phase || typeof phase !== 'object') continue;
        if (phase.rowKey != null) claimed.add(String(phase.rowKey));
        // mediation phase nests buyers[].rowKeys[] rather than a single rowKey
        if (Array.isArray(phase.buyers)) {
          for (const b of phase.buyers) {
            if (Array.isArray(b?.rowKeys)) {
              for (const rk of b.rowKeys) if (rk != null) claimed.add(String(rk));
            }
          }
        }
      }
    }
  }
  return claimed;
}

/**
 * buildOrphanReport(allRows, lifecycles)
 *
 * @param {Array} allRows - full unfiltered captured row set
 * @param {Array} lifecycles - output of buildSlotLifecycles(allRows)
 * @returns {{orphans:Array, duplicates:Array, counts:Object, reasons:Array}}
 */
export function buildOrphanReport(allRows, lifecycles) {
  try {
    if (!Array.isArray(allRows) || allRows.length === 0) return EMPTY_REPORT();

    const claimed = buildClaimedSet(lifecycles);

    // Index claimed rows' identity signals so unclaimed ad rows can be
    // checked for "shares an identity with a claimed row" (duplicate) vs
    // "shares nothing" (genuine orphan).
    const claimedCorrelatorIu = new Set();
    const claimedUrls = new Set();
    const claimedNurls = new Set();
    for (const row of allRows) {
      const key = rowKeyOf(row);
      if (key == null || !claimed.has(String(key))) continue;
      const ci = correlatorIuKey(row);
      if (ci) claimedCorrelatorIu.add(ci);
      const { url, nurl } = beaconIdentity(row);
      if (url) claimedUrls.add(url);
      if (nurl) claimedNurls.add(nurl);
    }

    const orphans = [];
    const duplicates = [];
    let adRowTotal = 0;

    for (const row of allRows) {
      const type = rowTypeOf(row);
      if (!AD_REQUEST_TYPES.has(type)) continue;
      adRowTotal += 1;

      const key = rowKeyOf(row);
      if (key == null) continue; // can't report a row we can't identify
      const keyStr = String(key);
      if (claimed.has(keyStr)) continue; // already accounted for

      const rowTs = tsOf(row);

      // Duplicate check: same correlator+iu, or same beacon URL / nurl, as
      // an already-claimed row.
      const ci = correlatorIuKey(row);
      const { url, nurl } = beaconIdentity(row);
      const isDupByCorrelatorIu = !!ci && claimedCorrelatorIu.has(ci);
      const isDupByUrl = !!url && claimedUrls.has(url);
      const isDupByNurl = !!nurl && claimedNurls.has(nurl);

      if (isDupByCorrelatorIu || isDupByUrl || isDupByNurl) {
        const reason = isDupByCorrelatorIu
          ? 'duplicate (same correlator+iu as a claimed request)'
          : isDupByNurl
            ? 'duplicate (same nurl as a claimed request)'
            : 'duplicate (same beacon URL as a claimed request)';
        duplicates.push({ rowKey: keyStr, type, of: ci || nurl || url || null, reason });
      } else {
        orphans.push({ rowKey: keyStr, type, ts: rowTs, reason: 'orphaned' });
      }
    }

    const counts = {
      total: adRowTotal,
      claimed: adRowTotal - orphans.length - duplicates.length,
      orphans: orphans.length,
      duplicates: duplicates.length,
    };

    const reasons = [];
    if (orphans.length > 0) {
      const byType = {};
      for (const o of orphans) byType[o.type] = (byType[o.type] || 0) + 1;
      for (const [type, count] of Object.entries(byType)) {
        reasons.push(`${count} orphaned ${type} row${count === 1 ? '' : 's'}`);
      }
    }
    if (duplicates.length > 0) {
      const gamDupes = duplicates.filter((d) => d.type === 'google').length;
      if (gamDupes > 0) {
        reasons.push(`${gamDupes} duplicate GAM request${gamDupes === 1 ? '' : 's'} (same correlator+iu)`);
      }
      const otherDupes = duplicates.length - gamDupes;
      if (otherDupes > 0) {
        reasons.push(`${otherDupes} duplicate non-GAM row${otherDupes === 1 ? '' : 's'}`);
      }
    }

    return { orphans, duplicates, counts, reasons };
  } catch {
    return EMPTY_REPORT();
  }
}

export default buildOrphanReport;
